import { useState } from 'react';
import { CheckCircle, UploadSimple, Spinner } from '@phosphor-icons/react';

const SCALE_LABELS = { 5: [1, 2, 3, 4, 5], 10: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] };

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="text-xs text-red-600 mt-1" data-testid="field-error">{msg}</p>;
}

export function FormRenderer({ form, imageUrl, onUploadFile, onSubmit, submitting, preview }) {
  const [answers, setAnswers] = useState({});
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingId, setUploadingId] = useState('');
  const [website, setWebsite] = useState('');

  const setValue = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const handleFile = async (field, file) => {
    if (!file) return;
    if (preview) { setValue(field.id, file.name); return; }
    setUploadingId(field.id);
    try {
      const path = await onUploadFile(file);
      setValue(field.id, path);
    } catch (e) {
      setErrors(prev => ({ ...prev, [field.id]: 'Não foi possível carregar o ficheiro.' }));
    } finally {
      setUploadingId('');
    }
  };

  const validate = () => {
    const errs = {};
    for (const f of form.fields || []) {
      if (['section_title', 'content_text', 'content_image'].includes(f.type)) continue;
      const v = answers[f.id];
      if (f.required && (v === undefined || v === '' || (Array.isArray(v) && v.length === 0))) {
        errs[f.id] = 'Este campo é obrigatório.';
      }
    }
    if (!consentAccepted) errs.consent = 'É necessário aceitar o consentimento para continuar.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (website.trim()) return;
    if (!validate()) return;
    const answerList = Object.entries(answers).filter(([, v]) => v !== undefined && v !== '').map(([field_id, value]) => ({ field_id, value }));
    onSubmit(answerList, consentAccepted, website);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="public-form">
      <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" data-testid="honeypot-field" />
      {(form.fields || []).map((field) => (
        <div key={field.id} data-testid={`form-field-${field.id}`}>
          {field.type === 'section_title' && (
            <h3 className="font-heading text-lg text-brand-green font-semibold pt-2 border-t border-brand-border/60 first:border-0 first:pt-0">{field.label}</h3>
          )}
          {field.type === 'content_text' && (
            <div>
              {field.label && <p className="font-medium text-brand-text text-sm mb-1">{field.label}</p>}
              <p className="text-sm text-brand-muted whitespace-pre-wrap">{field.help_text}</p>
            </div>
          )}
          {field.type === 'content_image' && field.image_path && (
            <img src={imageUrl(field.image_path)} alt={field.label || ''} className="w-full rounded-2xl object-cover max-h-72" />
          )}
          {!['section_title', 'content_text', 'content_image'].includes(field.type) && (
            <div>
              <label className="block text-sm font-medium text-brand-text mb-1">
                {field.label}{field.required && <span className="text-brand-gold-hover ml-0.5">*</span>}
              </label>
              {field.help_text && <p className="text-xs text-brand-muted mb-1.5">{field.help_text}</p>}

              {field.type === 'short_text' && (
                <input data-testid={`input-${field.id}`} type="text" placeholder={field.placeholder} value={answers[field.id] || ''} onChange={e => setValue(field.id, e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none text-sm bg-white" />
              )}
              {field.type === 'long_text' && (
                <textarea data-testid={`input-${field.id}`} placeholder={field.placeholder} rows={4} value={answers[field.id] || ''} onChange={e => setValue(field.id, e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none text-sm bg-white" />
              )}
              {(field.type === 'email' || field.type === 'phone' || field.type === 'number' || field.type === 'date') && (
                <input data-testid={`input-${field.id}`} type={field.type === 'phone' ? 'tel' : field.type} placeholder={field.placeholder} value={answers[field.id] || ''} onChange={e => setValue(field.id, e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none text-sm bg-white" />
              )}
              {field.type === 'single_choice' && (
                <div className="space-y-2">
                  {(field.options || []).map((opt, i) => (
                    <label key={i} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-brand-border cursor-pointer hover:border-brand-green transition-colors" data-testid={`option-${field.id}-${i}`}>
                      <input type="radio" name={field.id} checked={answers[field.id] === opt} onChange={() => setValue(field.id, opt)} className="accent-[#657161]" />
                      <span className="text-sm text-brand-text">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {field.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {(field.options || []).map((opt, i) => {
                    const arr = answers[field.id] || [];
                    return (
                      <label key={i} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-brand-border cursor-pointer hover:border-brand-green transition-colors" data-testid={`option-${field.id}-${i}`}>
                        <input type="checkbox" checked={arr.includes(opt)} onChange={e => setValue(field.id, e.target.checked ? [...arr, opt] : arr.filter(x => x !== opt))} className="accent-[#657161]" />
                        <span className="text-sm text-brand-text">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {field.type === 'dropdown' && (
                <select data-testid={`input-${field.id}`} value={answers[field.id] || ''} onChange={e => setValue(field.id, e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-brand-border focus:border-brand-green outline-none text-sm bg-white">
                  <option value="">Seleciona uma opção</option>
                  {(field.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                </select>
              )}
              {field.type === 'scale' && (
                <div className="flex gap-2 flex-wrap">
                  {SCALE_LABELS[field.scale_max || 5].map(n => (
                    <button key={n} type="button" data-testid={`scale-${field.id}-${n}`} onClick={() => setValue(field.id, n)}
                      className={`w-10 h-10 rounded-full border text-sm font-medium transition-colors ${answers[field.id] === n ? 'bg-brand-green text-white border-brand-green' : 'border-brand-border text-brand-text hover:border-brand-green'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              )}
              {field.type === 'consent_checkbox' && (
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={!!answers[field.id]} onChange={e => setValue(field.id, e.target.checked)} className="mt-0.5 accent-[#657161]" />
                  <span className="text-sm text-brand-text">{field.placeholder || 'Aceito'}</span>
                </label>
              )}
              {field.type === 'file_upload' && (
                <label className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl border border-dashed border-brand-border cursor-pointer hover:border-brand-green transition-colors bg-white" data-testid={`file-input-${field.id}`}>
                  {uploadingId === field.id ? <Spinner size={18} className="animate-spin text-brand-green" /> : answers[field.id] ? <CheckCircle size={18} className="text-brand-green" /> : <UploadSimple size={18} className="text-brand-muted" />}
                  <span className="text-sm text-brand-muted truncate">{answers[field.id] ? 'Ficheiro carregado' : 'Escolher ficheiro'}</span>
                  <input type="file" className="hidden" onChange={e => handleFile(field, e.target.files?.[0])} />
                </label>
              )}
              <FieldError msg={errors[field.id]} />
            </div>
          )}
        </div>
      ))}

      <div className="pt-2 border-t border-brand-border/60">
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="consent-checkbox">
          <input type="checkbox" checked={consentAccepted} onChange={e => setConsentAccepted(e.target.checked)} className="mt-0.5 accent-[#657161]" />
          <span className="text-sm text-brand-muted">{form.consent_text}</span>
        </label>
        <FieldError msg={errors.consent} />
      </div>

      <button type="submit" disabled={submitting} data-testid="submit-form-btn"
        className="w-full py-3.5 rounded-full bg-brand-green text-white font-medium text-sm hover:bg-brand-green-hover transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} />}
        {preview ? 'Pré-visualização (submissão desativada)' : 'Enviar candidatura'}
      </button>
    </form>
  );
}

export default FormRenderer;
